'use client';

import { cn } from '../../lib/utils/cn';
import { Flex } from '../atoms/flex';
import { type ComponentPropsWithoutRef, ReactNode } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { SliderProvider } from './slider-context';
import { useSliderValues } from './hooks/use-slider-values';

type SliderRootProps = ComponentPropsWithoutRef<typeof SliderPrimitive.Root>;

export type SliderFieldProps = Pick<
  SliderRootProps,
  | 'value'
  | 'defaultValue'
  | 'onValueChange'
  | 'onValueCommit'
  | 'min'
  | 'max'
  | 'step'
  | 'minStepsBetweenThumbs'
  | 'disabled'
> & {
  className?: string;
  children: ReactNode;
};

/**
 * Provides slider state to its children via context.
 * Use when you need SliderInputs.
 * For a bare slider, use Slider on its own.
 */
export const SliderField = ({
  value,
  defaultValue,
  onValueChange,
  onValueCommit,
  min = 0,
  max = 100,
  step = 1,
  minStepsBetweenThumbs = 0,
  disabled,
  className,
  children,
}: SliderFieldProps) => {
  const [values, handleValueChange] = useSliderValues({
    value,
    defaultValue,
    onValueChange,
  });

  return (
    <SliderProvider
      value={{
        values,
        min,
        max,
        step,
        minStepsBetweenThumbs,
        disabled,
        onValueChange: handleValueChange,
        onValueCommit,
      }}>
      <Flex className={cn('w-full flex-col gap-4', className)}>{children}</Flex>
    </SliderProvider>
  );
};

SliderField.displayName = 'SliderField';
